import { Button, DatePicker, Drawer, Input, Modal, Radio, Select } from 'antd';
import i18n from 'i18n';
import LOCALS from 'commons/locals';
import z from 'zod';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormLabel } from '../form-label';
import {
  getDefaultCountryCodeByStoreId,
  recycleConsignmentStoreList,
} from '../common';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { SelectOption } from 'types/base';
import { useCityList } from 'apis/home';
import { japaneseToGregorian } from 'components/member-add-edit/utils';
import dayjs from 'dayjs';
import { umsMemberCreateAPI, umsMemberEmailExists } from 'apis/ums';
import { isEmail } from 'validator';
import { UmsMember } from 'types/ums';
import { useToggle } from 'react-use';
import { LANGUAGE_MAP } from 'constant/language';
import {
  SOCIAL_MEDIA_MAP,
  SOCIAL_MEDIA_OPTION_LIST,
} from 'constant/social-media';
import { SHOP_MAP } from 'constant/shop';
import { GENDER_MAP, GENDER_OPTION_LIST } from 'constant/ums-member';

const createMemberSchema = z.object({
  createSource: z.number(), // 创建来源
  firstName: z.string().nonempty(), // 名
  lastName: z.string().nonempty(), // 姓
  firstNameKatakana: z.string(),
  lastNameKatakana: z.string(),
  gender: z.number(),
  language: z.string().nonempty(),
  email: z.email().superRefine(async (email, ctx) => {
    if (!isEmail(email)) return;
    const res = await umsMemberEmailExists(email);
    if (res.exist) {
      ctx.addIssue({
        code: 'custom',
        message: 'Email already exists',
      });
    }
  }),
  countryCode: z.string().nonempty(),
  phone: z.string().nonempty(),
  birthday: z.string().nullable(), // YYYY-MM-DD
  country: z.string().nonempty(), // 国家 code
  city: z.string().optional(), // 城市 id
  detailAddress: z.string(),
  socialName: z.string(), // 社交账号类型
  socialAccount: z.string(), // 社交账号
  idCertificate: z.string(),
  paymentDetails: z.string(),
  remark: z.string(),
});

export type CreateMemberSchemaType = z.infer<typeof createMemberSchema>;

const CreateMember = ({
  onSuccess,
  open,
  onClose,
  initCreateSource,
}: {
  onSuccess: (data: UmsMember) => void;
  onClose: () => void;
  open: boolean;
  initCreateSource?: number;
}) => {
  const { language } = useAppSelector(selectGlobalInfo);
  const isEn = language === LANGUAGE_MAP.EN;
  const isJa = language === LANGUAGE_MAP.JA;
  const { countryCodeOptions, countryOptions } = useSelector(selectGlobalInfo);
  const defaultCreateSource = initCreateSource || SHOP_MAP.GINZA;
  const defaultCountryCode =
    getDefaultCountryCodeByStoreId(defaultCreateSource);

  const { control, handleSubmit, reset, setValue } =
    useForm<CreateMemberSchemaType>({
      resolver: zodResolver(createMemberSchema),
      defaultValues: {
        createSource: defaultCreateSource,
        language: 'en',
        socialName: SOCIAL_MEDIA_MAP.WHATSAPP,
        firstName: '',
        lastName: '',
        firstNameKatakana: '',
        lastNameKatakana: '',
        gender: GENDER_MAP.UNKNOW,
        birthday: null,
        countryCode: defaultCountryCode,
        phone: '',
        detailAddress: '',
        idCertificate: '',
        paymentDetails: '',
        socialAccount: '',
        remark: '',
      },
    });

  const country = useWatch({ control, name: 'country' });
  const { cityList } = useCityList(country);

  const [submitData, setSubmitData] = useState<CreateMemberSchemaType | null>(
    null,
  );
  const [submitLoading, toggleSubmitLoading] = useToggle(false);

  const onSubmit = (data: CreateMemberSchemaType) => {
    setSubmitData(data);
  };

  const firstNameFormEl = useMemo(() => {
    return (
      <div className="flex gap-2 items-center">
        <FormLabel required>{i18n.t(LOCALS.first_name)}:</FormLabel>
        <Controller
          name="firstName"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              status={error ? 'error' : undefined}
              {...field}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
            />
          )}
        />
      </div>
    );
  }, [control]);

  const lastNameFormEl = useMemo(() => {
    return (
      <div className="flex gap-2 items-center">
        <FormLabel required>{i18n.t(LOCALS.last_name)}:</FormLabel>
        <Controller
          name="lastName"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              status={error ? 'error' : undefined}
              {...field}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
            />
          )}
        />
      </div>
    );
  }, [control]);

  const firstNameKatakanaFormEl = useMemo(() => {
    return (
      <div className="flex gap-2 items-center">
        <FormLabel>{i18n.t(LOCALS.first_name_katakana)}:</FormLabel>
        <Controller
          name="firstNameKatakana"
          control={control}
          render={({ field }) => (
            <Input {...field} placeholder={i18n.t(LOCALS.please_enter) || ''} />
          )}
        />
      </div>
    );
  }, [control]);

  const lastNameKatakanaFormEl = useMemo(() => {
    return (
      <div className="flex gap-2 items-center">
        <FormLabel>{i18n.t(LOCALS.last_name_katakana)}:</FormLabel>
        <Controller
          name="lastNameKatakana"
          control={control}
          render={({ field }) => (
            <Input {...field} placeholder={i18n.t(LOCALS.please_enter) || ''} />
          )}
        />
      </div>
    );
  }, [control]);

  return (
    <Drawer
      open={open}
      title={i18n.t(LOCALS.member_sign_up)}
      onClose={onClose}
      width={800}
    >
      <Modal
        open={!!submitData}
        onCancel={() => setSubmitData(null)}
        title={i18n.t(LOCALS.confirm_submit)}
        confirmLoading={submitLoading}
        onOk={async () => {
          if (!submitData) return;
          toggleSubmitLoading(true);
          umsMemberCreateAPI({
            ...submitData,
            birthday: submitData.birthday ? submitData.birthday : undefined,
          })
            .then((res) => {
              onSuccess(res);
              setSubmitData(null);
              onClose();
            })
            .finally(() => {
              toggleSubmitLoading(false);
            });
        }}
      ></Modal>
      <form className="space-y-6">
        <div className="flex items-center gap-2">
          <FormLabel required>{i18n.t(LOCALS.created_from)}:</FormLabel>
          <Controller
            name="createSource"
            control={control}
            render={({ field }) => (
              <Select
                disabled={!!initCreateSource}
                {...field}
                className="w-full"
                options={recycleConsignmentStoreList}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          {isEn ? firstNameFormEl : lastNameFormEl}
          {isEn ? lastNameFormEl : firstNameFormEl}
        </div>

        <div className="grid grid-cols-2 gap-2">
          {isEn ? firstNameKatakanaFormEl : lastNameKatakanaFormEl}
          {isEn ? lastNameKatakanaFormEl : firstNameKatakanaFormEl}
        </div>

        <div className="flex items-center gap-2">
          <FormLabel required>{i18n.t(LOCALS.gender)}:</FormLabel>
          <Controller
            name="gender"
            control={control}
            render={({ field }) => (
              <Radio.Group
                {...field}
                className="w-full"
                optionType="button"
                buttonStyle="solid"
              >
                {GENDER_OPTION_LIST.map(({ value, label }) => (
                  <Radio value={value} key={value}>
                    {label}
                  </Radio>
                ))}
              </Radio.Group>
            )}
          />
        </div>

        <div className="flex items-center gap-2">
          <FormLabel required>{i18n.t(LOCALS.CxeLXsjeRo)}:</FormLabel>
          <Controller
            name="language"
            control={control}
            render={({ field }) => (
              <Radio.Group
                {...field}
                className="w-full"
                optionType="button"
                buttonStyle="solid"
              >
                <Radio value="en">{i18n.t('en')}</Radio>
                <Radio value="ja">{i18n.t('ja')}</Radio>
                <Radio value="zh_CN">{i18n.t('zh_CN')}</Radio>
                <Radio value="zh_TW">{i18n.t('zh_TW')}</Radio>
              </Radio.Group>
            )}
          />
        </div>

        <div className="flex items-center gap-2">
          <FormLabel required>{i18n.t(LOCALS.email)}:</FormLabel>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <div className="w-full relative">
                  <Input
                    {...field}
                    status={error ? 'error' : undefined}
                    placeholder={i18n.t(LOCALS.please_enter) || ''}
                  />
                  {error && (
                    <div className="text-red-500 absolute left-0">
                      {error.message}
                    </div>
                  )}
                </div>
              );
            }}
          />
        </div>

        <div className="flex items-center gap-2">
          <FormLabel required>{i18n.t(LOCALS.phone_number)}:</FormLabel>

          <Controller
            name="countryCode"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                showSearch
                status={error ? 'error' : undefined}
                filterOption={(
                  input: string,
                  option: SelectOption | undefined,
                ) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                className="w-56"
                options={countryCodeOptions}
              />
            )}
          />

          <Controller
            name="phone"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                status={error ? 'error' : undefined}
                placeholder={i18n.t(LOCALS.please_enter_without_hyphen) || ''}
              />
            )}
          />
        </div>

        <div className="flex items-center gap-2">
          <FormLabel>{i18n.t(LOCALS.birthday)}:</FormLabel>
          {isJa && (
            <Input
              className="w-56"
              placeholder="R6/7/12"
              onBlur={(e) => {
                const val = e.target.value;
                if (val) {
                  const gregorianDate = japaneseToGregorian(val);
                  gregorianDate && setValue('birthday', gregorianDate);
                }
              }}
            />
          )}
          <Controller
            name="birthday"
            control={control}
            render={({ field }) => (
              <DatePicker
                className="w-full"
                placeholder="2024-07-12"
                value={field.value ? dayjs(field.value) : null}
                onChange={(value) => {
                  field.onChange(value ? value.format('YYYY-MM-DD') : null);
                }}
              />
            )}
          />
        </div>

        <div className="flex items-center gap-2">
          <FormLabel required>{i18n.t(LOCALS.country_region)}:</FormLabel>
          <Controller
            name="country"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                status={error ? 'error' : undefined}
                options={countryOptions}
                showSearch
                filterOption={(
                  input: string,
                  option: SelectOption | undefined,
                ) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                className="w-full"
                placeholder={i18n.t(LOCALS.please_select) || ''}
                onChange={(value) => {
                  field.onChange(value);
                  setValue('city', undefined);
                }}
              />
            )}
          />
        </div>

        <div className="flex items-center gap-2">
          <FormLabel>{i18n.t(LOCALS.state_province_city)}:</FormLabel>
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                showSearch
                filterOption={(
                  input: string,
                  option: SelectOption | undefined,
                ) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                placeholder={i18n.t(LOCALS.BtxdTZwomQ) || ''}
                className="w-full"
                options={cityList.map(({ name, id }) => {
                  return {
                    value: id.toString(),
                    label: name,
                  };
                })}
              />
            )}
          />
        </div>

        <div className="flex items-center gap-2">
          <FormLabel className="shrink-0 self-start">
            {i18n.t(LOCALS.detail_address)}:
          </FormLabel>
          <Controller
            name="detailAddress"
            control={control}
            render={({ field }) => (
              <Input.TextArea
                {...field}
                placeholder={i18n.t(LOCALS.please_enter) || ''}
                rows={3}
              />
            )}
          />
        </div>

        <div className="flex items-center gap-2">
          <FormLabel>{i18n.t(LOCALS.social_media)}:</FormLabel>
          <Controller
            name="socialName"
            control={control}
            render={({ field }) => (
              <Select
                className="w-56"
                {...field}
                options={SOCIAL_MEDIA_OPTION_LIST}
              />
            )}
          />
          <Controller
            name="socialAccount"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder={i18n.t(LOCALS.please_enter) || ''}
              />
            )}
          />
        </div>

        <div className="flex items-center gap-2">
          <FormLabel>{i18n.t(LOCALS.id_certificate)}:</FormLabel>
          <Controller
            name="idCertificate"
            control={control}
            render={({ field }) => (
              <Input.TextArea
                {...field}
                placeholder={i18n.t(LOCALS.please_enter) || ''}
              />
            )}
          />
        </div>

        <div className="flex items-center gap-2">
          <FormLabel>{i18n.t(LOCALS.payment_details)}:</FormLabel>
          <Controller
            name="paymentDetails"
            control={control}
            render={({ field }) => (
              <Input.TextArea
                {...field}
                placeholder={i18n.t(LOCALS.please_enter) || ''}
              />
            )}
          />
        </div>

        <div className="flex items-center gap-2">
          <FormLabel className="shrink-0 self-start">
            {i18n.t(LOCALS.note)}:
          </FormLabel>
          <Controller
            name="remark"
            control={control}
            render={({ field }) => (
              <Input.TextArea
                {...field}
                placeholder={i18n.t(LOCALS.please_enter) || ''}
                rows={3}
              />
            )}
          />
        </div>

        <div className="flex gap-2 justify-center">
          <Button type="primary" onClick={handleSubmit(onSubmit)}>
            {i18n.t(LOCALS.submit)}
          </Button>
          <Button onClick={() => reset()}>{i18n.t(LOCALS.reset)}</Button>
        </div>
      </form>
    </Drawer>
  );
};

export default CreateMember;
